// src/pages/HomePage.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  IconButton,
  Button,
  Drawer,
  Slider,
  Tabs,
  Tab,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useLocation } from 'react-router-dom';

import LeafletMapNearbyUsers from '../components/LeafletMapNearbyUsers';
import ArtworkList from '../components/ArtworkList';
import subcategorySchema from '../utils/subcategorySchema';
import ArtworkSearchFiltersModal from '../components/ArtworkSearchFiltersModal';
import {
  DEFAULT_DISCOVERY_POSITION,
  getInitialDiscoveryPosition,
  writeStoredDiscoveryPosition,
} from '../utils/discoveryLocation';

// Images + exact text content from HomePageInfoSlider
import ArtistImg from '../assets/Artist.png';
import HostImg from '../assets/Host.png';
import BuyerImg from '../assets/Buyer.png';
import OurRoleImg from '../assets/OurRole.png';

const ARTWORK_STATUSES = ["available", "hosted", "approved", "sold", "featured"];

function useInViewSections(sectionIds, rootMargin = '-25% 0px -55% 0px') {
  const [activeId, setActiveId] = useState(sectionIds?.[0] || null);

  useEffect(() => {
    if (!sectionIds?.length) return;

    const els = sectionIds
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => (b.intersectionRatio || 0) - (a.intersectionRatio || 0));
        if (visible[0]?.target?.id) setActiveId(visible[0].target.id);
      },
      { root: null, rootMargin, threshold: [0.05, 0.15, 0.3, 0.5, 0.7] }
    );

    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [sectionIds, rootMargin]);

  return activeId;
}

// Fixed navbar height offset (tweak if your Navbar is taller)
//const NAV_OFFSET = 76;

function scrollToId(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function scrollToIdWithRetries(id, tries = 12, intervalMs = 50) {
  let n = 0;
  const t = setInterval(() => {
    const el = document.getElementById(id);
    if (el) {
      clearInterval(t);
      scrollToId(id);
      return;
    }
    n += 1;
    if (n >= tries) clearInterval(t);
  }, intervalMs);
}

function BigPictureSection({ id, overlay = 'rgba(2,6,23,0.55)', children }) {
  return (
    <Box
      id={id}
      component="section"
      sx={{
        minHeight: '100vh',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        pt: { xs: '64px', md: '72px' },
        pb: { xs: 6, md: 8 },
        scrollMarginTop: { xs: '52px', md: '52px' },    
        fontFamily: '"Helvetica", Arial, sans-serif',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: overlay,
          zIndex: 0,
        }}
      />
      <Box sx={{ position: 'relative', zIndex: 1, width: '100%' }}>{children}</Box>
    </Box>
  );
}

function SlidePanel({
  side = 'left', // left | right
  title,
  subtitle,
  imageSrc,
  imageAlt,
  children,
  inView = false,
  tint = 'invert', // 'invert' (black->white) | 'none' (already-white)
}) {
  const fromX = side === 'left' ? -48 : 48;

  return (
    <Box
      sx={{
        transform: inView ? 'translateX(0px)' : `translateX(${fromX}px)`,
        opacity: inView ? 1 : 0,
        transition: 'transform 900ms ease, opacity 900ms ease',
        borderRadius: 3,
        border: '1px solid rgba(255,255,255,0.28)',
        bgcolor: 'rgba(2,6,23,0.70)', // darker for readability
        backdropFilter: 'blur(10px)',
        boxShadow: '0 24px 70px rgba(0,0,0,0.45)',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: {
            xs: 'column',
            md: side === 'left' ? 'row' : 'row-reverse',
          },
        }}
      >
        {imageSrc && (
          <Box
            sx={{
              flex: '0 0 auto',
              width: { xs: '100%', md: 420 },
              p: { xs: 2, md: 2.5 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderBottom: { xs: '1px solid rgba(255,255,255,0.18)', md: 'none' },
              borderRight:
                side === 'left'
                  ? { xs: 'none', md: '1px solid rgba(255,255,255,0.18)' }
                  : 'none',
              borderLeft:
                side === 'right'
                  ? { xs: 'none', md: '1px solid rgba(255,255,255,0.18)' }
                  : 'none',
            }}
          >
            <Box
              component="img"
              src={imageSrc}
              alt={imageAlt || ''}
              sx={{
                width: '100%',
                maxWidth: 360,
                height: 'auto',
                //backgroundColor: 'transparent',
                //filter:
                //  tint === 'invert'
                //    ? 'invert(1) brightness(1.08) contrast(1.08)'
                //    : 'none',
                mixBlendMode: 'screen',
                opacity: 0.95,
              }}
            />
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
            p: { xs: 2.5, md: 3.5 },
            color: '#fff',
            // If you want LESS boldness overall, reduce these:
            fontWeight: 650,
            '& *': { fontWeight: 650 },
          }}
        >
          {title && (
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                mb: 1,
                fontFamily: '"Helvetica", Arial, sans-serif',
              }}
            >
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography
              variant="subtitle1"
              sx={{
                color: 'rgba(255,255,255,0.85)',
                mb: 2,
                fontFamily: '"Helvetica", Arial, sans-serif',
                fontWeight: 650,
              }}
            >
              {subtitle}
            </Typography>
          )}
          <Box
            sx={{
              fontFamily: '"Helvetica", Arial, sans-serif',
              color: 'inherit',
              fontWeight: 550,
              '& strong': { fontWeight: 750 },
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function useSectionInView(id, options) {
  const [inView, setInView] = React.useState(false);

  React.useEffect(() => {
    const el = document.getElementById(id);
    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      {
        root: null,
        // Looser margins helps iOS trigger earlier and more reliably
        rootMargin: options?.rootMargin ?? '-10% 0px -60% 0px',
        threshold: options?.threshold ?? 0.08,
      }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [id, options?.rootMargin, options?.threshold]);

  return inView;
}

export default function HomePage({ initialSection = 'intro' }) {
  const location = useLocation();

  const [distance, setDistance] = useState(50);
  const [position, setPosition] = useState(() => getInitialDiscoveryPosition());
  const [viewMode, setViewMode] = useState('artists');
  const [nearbyArtworks, setNearbyArtworks] = useState([]);
  const [nearbyArtistArtworks, setNearbyArtistArtworks] = useState([]);
  const [nearbyHostedArtworks, setNearbyHostedArtworks] = useState([]);

  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategories, setSelectedSubcategories] = useState({});
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [statusFilter, setStatusFilter] = useState([]);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [nearbySubTab, setNearbySubTab] = useState(0);

  const [artistGetStartedOpen, setArtistGetStartedOpen] = useState(false);
  const [hostGetStartedOpen, setHostGetStartedOpen] = useState(false);
  const LAST_SECTION_KEY = 'artflow_home_last_section';
  const artistsInView = useSectionInView('artists');
  const hostsInView   = useSectionInView('hosts');
  const buyersInView  = useSectionInView('buyers');
  const roleInView    = useSectionInView('role');
  const whatInView    = useSectionInView('what');

  function getLastSection() {
    try { return sessionStorage.getItem(LAST_SECTION_KEY) || ''; } catch { return ''; }
  }

  function setLastSection(id) {
    try { sessionStorage.setItem(LAST_SECTION_KEY, id); } catch {}
  }

  // IMPORTANT: include get-started sections so inView animation works there too
  const sectionIds = useMemo(
    () => ['intro', 'what', 'artists', 'artist-get-started', 'hosts', 'host-get-started', 'buyers', 'role', 'discover'],
    []
  );
  const activeId = useInViewSections(sectionIds);
  
  useEffect(() => {
    if (!activeId) return;

    // Don’t store get-started drawer pseudo-sections if you don’t want
    // (you can remove this if you DO want those remembered)
    if (activeId === 'artist-get-started' || activeId === 'host-get-started') return;

    setLastSection(activeId);
  }, [activeId]);

  // unified scroll behavior:
  const didInitScroll = useRef(false);

  useEffect(() => {
    const hash = (location.hash || '').replace('#', '');

    // If no hash, prefer last saved section.
    // Also: if initialSection is "intro" (default route), we still prefer last section on reload.
    const saved = getLastSection();
    const initial = initialSection || 'intro';

    const target = hash || saved || initial || 'intro';

    // On first mount: scroll once (after DOM settles)
    if (!didInitScroll.current) {
      didInitScroll.current = true;

      // Use a slightly longer delay to survive iOS Safari “reflow after paint”
      setTimeout(() => scrollToIdWithRetries(target, 18, 60), 120);
      return;
    }

    // Subsequent hash changes: scroll to new hash
    if (hash) {
      setTimeout(() => scrollToIdWithRetries(hash, 18, 60), 60);
    }
  }, [location.hash, initialSection]);

  useEffect(() => {
    setViewMode(nearbySubTab === 0 ? 'artists' : 'hosts');
  }, [nearbySubTab]);

  useEffect(() => {
    if (!position) return;
    writeStoredDiscoveryPosition(position);
  }, [position]);

  useEffect(() => {
    if (!navigator.geolocation) {
      setPosition((current) => current || DEFAULT_DISCOVERY_POSITION);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setPosition({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
      },
      () => {
        setPosition((current) => current || DEFAULT_DISCOVERY_POSITION);
      },
      {
        enableHighAccuracy: false,
        maximumAge: 5 * 60 * 1000,
        timeout: 4000,
      }
    );
  }, []);

  useEffect(() => {
    const allArtworks = viewMode === 'artists' ? nearbyArtistArtworks : nearbyHostedArtworks;
    if (!allArtworks) return;

    const filtered = allArtworks.filter((art) => {
      if (statusFilter.length > 0 && !statusFilter.includes(art?.status)) return false;
      if (selectedCategory && art.artwork_type !== selectedCategory) return false;
      const numericPrice = Number(art?.price);
      if (minPrice !== '' && (!Number.isFinite(numericPrice) || numericPrice < Number(minPrice))) return false;
      if (maxPrice !== '' && (!Number.isFinite(numericPrice) || numericPrice > Number(maxPrice))) return false;

      if (selectedCategory && subcategorySchema[selectedCategory]) {
        for (const subKey of Object.keys(subcategorySchema[selectedCategory])) {
          if (
            selectedSubcategories[subKey] &&
            art.subcategories?.[subKey] !== selectedSubcategories[subKey]
          ) {
            return false;
          }
        }
      }
      return true;
    });

    setNearbyArtworks(filtered);
  }, [viewMode, nearbyArtistArtworks, nearbyHostedArtworks, selectedCategory, selectedSubcategories, statusFilter, minPrice, maxPrice]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        fontFamily: '"Helvetica", Arial, sans-serif',
        color: '#fff',
        backgroundColor: 'transparent',
      }}
    >
      {/* INTRO */}
      <BigPictureSection id="intro" overlay="rgba(2,6,23,0.55)">
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 900,
              mb: 2,
              textShadow: '0 22px 60px rgba(0,0,0,0.65)',
              fontFamily: '"Helvetica", Arial, sans-serif',
            }}
          >
            Welcome to ARTflow
          </Typography>

          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255,255,255,0.92)',
              maxWidth: 820,
              mx: 'auto',
              fontFamily: '"Helvetica", Arial, sans-serif',
            }}
          >
            Discover, host, and buy artwork from local artists. Explore artworks in your area using interactive maps.
          </Typography>

          <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontWeight: 800 }}>What Is ARTflow?</Typography>
            <IconButton
              onClick={() => scrollToId('what')}
              sx={{
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.35)',
                bgcolor: 'rgba(255,255,255,0.08)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.14)' },
              }}
            >
              <KeyboardArrowDownIcon />
            </IconButton>
          </Box>
        </Container>
      </BigPictureSection>

      {/* WHAT ARTflow IS */}
      <BigPictureSection id="what" overlay="rgba(2,6,23,0.55)">
        <Container maxWidth="lg">
          <SlidePanel side="left" title="" subtitle="" inView={whatInView}>
            <Typography sx={{ fontSize: 20, lineHeight: 1.8 }}>
              <strong>ARTflow</strong> is a neighborhood art-hosting platform that connects artists with vetted local venues—cafés,
              studios, galleries, small businesses, private homes, and community spaces—interested in temporarily exhibiting original
              artwork. Think of it as a structured way to place artwork in high-visibility local spaces without undermining existing
              gallery relationships.
            </Typography>
          </SlidePanel>

          <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontWeight: 800 }}>How Does ARTflow Benefit Artists?</Typography>
            <IconButton
              onClick={() => scrollToId('artists')}
              sx={{
                color: '#111827',
                border: '1px solid rgba(17,24,39,0.25)',
                bgcolor: 'rgba(255,255,255,0.75)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
              }}
            >
              <KeyboardArrowDownIcon />
            </IconButton>
          </Box>
        </Container>
      </BigPictureSection>

      {/* ARTISTS */}
      <BigPictureSection id="artists" overlay="rgba(59,130,246,0.20)">
        <Container maxWidth="lg">
          <SlidePanel
            side="right"
            title="Artists"
            subtitle="Showcase your work in real spaces and online, and earn from every sale."
            imageSrc={ArtistImg}
            imageAlt="Artists"
            inView={artistsInView}
          >
            <Typography sx={{ fontWeight: 800, mb: 1 }}>Key Benefits:</Typography>
            <ul style={{ marginTop: 0, lineHeight: 1.8 }}>
              <li>Upload your artwork, approve host venues, and set pricing.</li>
              <li>Reach more buyers, beyond traditional galleries.</li>
              <li>You choose where your work is displayed.</li>
              <li>Earn revenue minus a small commission.</li>
              <li>Pay nothing upfront—only when you sell.</li>
              <li>We'll handle promotion and protect your art.</li>
            </ul>

            <Button
              variant="contained"
              onClick={() => setArtistGetStartedOpen(true)}
              sx={{ mt: 1.5, borderRadius: 999, fontWeight: 800, backgroundColor: '#0f1413ff' }}
            >
              Get Started as an Artist
            </Button>
          </SlidePanel>

          <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontWeight: 800 }}>How Does ARTflow Benefit Art Hosts?</Typography>
            <IconButton
              onClick={() => scrollToId('hosts')}
              sx={{
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.35)',
                bgcolor: 'rgba(255,255,255,0.10)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.16)' },
              }}
            >
              <KeyboardArrowDownIcon />
            </IconButton>
          </Box>
        </Container>
      </BigPictureSection>

      {/* HOSTS */}
      <BigPictureSection id="hosts" overlay="rgba(16,185,129,0.18)">
        <Container maxWidth="lg">
          <SlidePanel
            side="left"
            title="Art Hosts"
            subtitle="Turn your space into a living gallery."
            imageSrc={HostImg}
            imageAlt="Art Hosts"
            inView={hostsInView}
          >
            <Typography sx={{ fontWeight: 800, mb: 1 }}>Host Highlights:</Typography>
            <ul style={{ marginTop: 0, lineHeight: 1.8 }}>
              <li>Choose artwork from our curated catalog.</li>
              <li>Display rotating artwork, attract foot traffic, and earn commission on sales.</li>
              <li>Get rated based on your performance.</li>
              <li>We’ll help coordinate deliveries and marketing.</li>
              <li>All sales go through the platform—no awkward money talks.</li>
            </ul>

            <Button
              variant="contained"
              onClick={() => setHostGetStartedOpen(true)}
              sx={{ mt: 1.5, borderRadius: 999, fontWeight: 800, backgroundColor: '#0f1413ff' }}
            >
              Get Started as an Art Host
            </Button>
          </SlidePanel>

          <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontWeight: 800 }}>How Does ARTflow Benefit Art Buyers?</Typography>
            <IconButton
              onClick={() => scrollToId('buyers')}
              sx={{
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.35)',
                bgcolor: 'rgba(255,255,255,0.10)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.16)' },
              }}
            >
              <KeyboardArrowDownIcon />
            </IconButton>
          </Box>
        </Container>
      </BigPictureSection>

      {/* BUYERS */}
      <BigPictureSection id="buyers" overlay="rgba(168,85,247,0.16)">
        <Container maxWidth="lg">
          <SlidePanel
            side="right"
            title="Art Buyers"
            subtitle="Discover local art near you—online or in person."
            imageSrc={BuyerImg}
            imageAlt="Art Buyers"
            inView={buyersInView}
          >
            <Typography sx={{ fontWeight: 800, mb: 1 }}>Buyer Perks:</Typography>
            <ul style={{ marginTop: 0, lineHeight: 1.8 }}>
              <li>Browse, visit, and buy with ease.</li>
              <li>Find and explore art that fits your space and taste.</li>
              <li>Visit hosts nearby to view before you buy.</li>
              <li>Purchase through the site or in-person.</li>
              <li>Home delivery or on-the-spot pickup available.</li>
            </ul>
          </SlidePanel>

          <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontWeight: 800 }}>What ARTflow Does?</Typography>
            <IconButton
              onClick={() => scrollToId('role')}
              sx={{
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.35)',
                bgcolor: 'rgba(255,255,255,0.10)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.16)' },
              }}
            >
              <KeyboardArrowDownIcon />
            </IconButton>
          </Box>
        </Container>
      </BigPictureSection>

      {/* OUR ROLE */}
      <BigPictureSection id="role" overlay="rgba(251,146,60,0.16)">
        <Container maxWidth="lg">
          <SlidePanel
            side="left"
            title="Our Role"
            subtitle="We’re the connector, curator, and protector of your art experience."
            imageSrc={OurRoleImg}
            imageAlt="What We Do"
            inView={roleInView}
          >
            <Typography sx={{ fontWeight: 800, mb: 1 }}>We Provide:</Typography>
            <ul style={{ marginTop: 0, lineHeight: 1.8 }}>
              <li>A digital platform that brings everyone together.</li>
              <li>Secure payment processing.</li>
              <li>Ratings, analytics, and support.</li>
              <li>Strategic guidance for hosts to collaborate by neighborhood.</li>
            </ul>
          </SlidePanel>

          <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontWeight: 900 }}>Discover Nearby Art</Typography>
            <IconButton
              onClick={() => scrollToId('discover')}
              sx={{
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.35)',
                bgcolor: 'rgba(255,255,255,0.10)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.16)' },
              }}
            >
              <KeyboardArrowDownIcon />
            </IconButton>
          </Box>
        </Container>
      </BigPictureSection>

      {/* DISCOVER NEARBY ART */}
      <Box
        id="discover"
        component="section"
        sx={{
          minHeight: '100vh',
          position: 'relative',
          pt: { xs: '64px', md: '72px' },
          pb: { xs: 6, md: 8 },
          scrollMarginTop: { xs: '52px', md: '52px' },
          fontFamily: '"Helvetica", Arial, sans-serif',
        }}
      >
        <Container maxWidth="xl">
          <Typography variant="h4" sx={{ fontWeight: 900, mb: 2, textAlign: 'center' }}>
            Nearby Artists and Hosts
          </Typography>

          <Box
            sx={{
              maxWidth: 460,
              mx: 'auto',
              mb: 2.5,
              p: 2,
              borderRadius: 3,
              bgcolor: 'rgba(255,255,255,0.14)',
              border: '1px solid rgba(255,255,255,0.20)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Typography sx={{ fontWeight: 800, mb: 1 }}>Search radius (miles)</Typography>
            <Slider value={distance} min={1} max={200} step={1} valueLabelDisplay="auto" onChange={(e, val) => setDistance(val)} />
          </Box>

          {position && (
            <LeafletMapNearbyUsers
              roleFilter={['artist', 'host']}
              distance={distance}
              position={[position.lat, position.lng]}
              buyer={[{ location: { latitude: position.lat, longitude: position.lng } }]}
              setNearbyArtistArtworks={setNearbyArtistArtworks}
              setNearbyHostedArtworks={setNearbyHostedArtworks}
              setPosition={(nextPosition) => {
                if (!Array.isArray(nextPosition) || nextPosition.length !== 2) return;
                setPosition({ lat: nextPosition[0], lng: nextPosition[1] });
              }}
              fallbackToDefaultOnEmpty
            />
          )}

          <Box
            sx={{
              mt: 3,
              border: '1px solid rgba(255,255,255,0.20)',
              borderRadius: 2,
              backgroundColor: 'rgba(255,255,255,0.10)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Tabs
              value={viewMode === 'artists' ? 0 : 1}
              onChange={(_, v) => setNearbySubTab(v)}
              variant="scrollable"
              allowScrollButtonsMobile
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minHeight: 44,
                  fontWeight: 800,
                  color: 'rgba(255,255,255,0.80)',
                },
                '& .Mui-selected': { color: '#fff' },
              }}
            >
              <Tab label="Artworks by Nearby Artists" />
              <Tab label="Artworks Hosted by Nearby Hosts" />
            </Tabs>
          </Box>

          <Box
            sx={{
              mt: 2,
              p: { xs: 1.5, md: 2 },
              borderRadius: 3,
              bgcolor: 'rgba(255,255,255,0.10)',
              border: '1px solid rgba(255,255,255,0.16)',
              backdropFilter: 'blur(10px)',
            }}
          >
            {viewMode === 'artists' && (
              <>
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setFilterModalOpen(true)}
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.10)',
                      borderColor: 'rgba(255,255,255,0.16)',
                      color: '#fff',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.10)',
                        borderColor: 'rgba(255,255,255,0.24)',
                      },
                    }}
                  >
                    SEARCH FILTERS
                  </Button>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
                  Artworks by Nearby Artists
                </Typography>
                <ArtworkList
                  artworks={nearbyArtworks}
                  userRole="guest"
                  containerSx={{
                    gridTemplateColumns: 'repeat(auto-fit, 360px)',
                    justifyContent: 'center',
                    padding: 0,
                    mt: 2,
                  }}
                />
              </>
            )}

            {viewMode === 'hosts' && (
              <>
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setFilterModalOpen(true)}
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.10)',
                      borderColor: 'rgba(255,255,255,0.16)',
                      color: '#fff',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.10)',
                        borderColor: 'rgba(255,255,255,0.24)',
                      },
                    }}
                  >
                    SEARCH FILTERS
                  </Button>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
                  Artworks Hosted by Nearby Hosts
                </Typography>
                <ArtworkList
                  artworks={nearbyArtworks}
                  userRole="guest"
                  containerSx={{
                    gridTemplateColumns: 'repeat(auto-fit, 360px)',
                    justifyContent: 'center',
                    padding: 0,
                    mt: 2,
                  }}
                />
              </>
            )}
          </Box>
        </Container>
      </Box>

      <ArtworkSearchFiltersModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        statuses={ARTWORK_STATUSES}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        selectedCategory={selectedCategory}
        onCategoryChange={(value) => {
          setSelectedCategory(value);
          setSelectedSubcategories({});
        }}
        selectedSubcategories={selectedSubcategories}
        onSubcategoryChange={(sub, value) => {
          setSelectedSubcategories((prev) => ({ ...prev, [sub]: value }));
        }}
        minPrice={minPrice}
        maxPrice={maxPrice}
        onMinPriceChange={setMinPrice}
        onMaxPriceChange={setMaxPrice}
        onClearAll={() => {
          setStatusFilter([]);
          setSelectedCategory('');
          setSelectedSubcategories({});
          setMinPrice('');
          setMaxPrice('');
        }}
      />

      {/* Artist Get Started Drawer */}
      <Drawer anchor="left" open={artistGetStartedOpen} onClose={() => setArtistGetStartedOpen(false)}>
        <Box
          sx={{
            width: { xs: '100vw', sm: 520 },
            p: 2.5,
            fontFamily: '"Helvetica", Arial, sans-serif',
            color: '#fff',
            bgcolor: 'rgba(2,6,23,0.92)',
            height: '100%',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 900, mb: 2 }}>
            Creating an Artist Account
          </Typography>

          <ol style={{ paddingLeft: '1.25rem', lineHeight: 1.9, marginTop: 0, fontWeight: 800 }}>
            <li><strong>Sign up</strong> and verify your email.</li>
            <li><strong>Complete your Artist Profile</strong>: short bio, preferred art types/mediums, portfolio links, profile image.</li>
            <li><strong>Upload artworks</strong> with clear titles, descriptions, and multiple high-quality images.</li>
            <li><strong>Set hosting preferences</strong>: size limits, availability, location.</li>
            <li><strong>Enable payouts</strong> via Stripe Connect onboarding.</li>
            <li><strong>Respond to Host Requests</strong> and track approvals in your dashboard.</li>
          </ol>

          <Typography variant="body2" sx={{ mt: 1, fontWeight: 800 }}>
            Tip: Consistent, well-lit photos significantly improve discovery and approvals.
          </Typography>

          <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
            <Button
              variant="contained"
              sx={{ borderRadius: 999, fontWeight: 900 }}
              onClick={() => {
                setArtistGetStartedOpen(false);
                scrollToId('hosts');
              }}
            >
              Continue
            </Button>
            <Button
              variant="outlined"
              sx={{ borderRadius: 999, fontWeight: 900, color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }}
              onClick={() => setArtistGetStartedOpen(false)}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Host Get Started Drawer */}
      <Drawer anchor="left" open={hostGetStartedOpen} onClose={() => setHostGetStartedOpen(false)}>
        <Box
          sx={{
            width: { xs: '100vw', sm: 560 },
            p: 2.5,
            fontFamily: '"Helvetica", Arial, sans-serif',
            color: '#fff',
            bgcolor: 'rgba(2,6,23,0.92)',
            height: '100%',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 900, mb: 2 }}>
            Creating a Host Account
          </Typography>

          <ol style={{ paddingLeft: '1.25rem', lineHeight: 1.9, marginTop: 0, fontWeight: 800 }}>
            <li><strong>Register as a Host</strong> and verify your email.</li>
            <li><strong>Complete your Venue Profile</strong>: venue description, display spaces, lighting &amp; environmental controls, staffing, foot traffic, curation philosophy, up to 10 venue photos.</li>
            <li><strong>Define hosting preferences</strong>: art types, size limits, scheduling windows.</li>
            <li><strong>Enable payouts</strong> via Stripe Connect.</li>
            <li><strong>Use the map</strong> to discover nearby artists and send Host Requests for specific artworks.</li>
            <li><strong>Prepare your venue</strong>: ensure mounting hardware and lighting are ready before installation.</li>
          </ol>

          <Typography variant="body2" sx={{ mt: 1, fontWeight: 800 }}>
            Tip: Detailed venue profiles attract better matches and faster approvals.
          </Typography>

          <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
            <Button
              variant="contained"
              sx={{ borderRadius: 999, fontWeight: 900 }}
              onClick={() => {
                setHostGetStartedOpen(false);
                scrollToId('buyers');
              }}
            >
              Continue
            </Button>
            <Button
              variant="outlined"
              sx={{ borderRadius: 999, fontWeight: 900, color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }}
              onClick={() => setHostGetStartedOpen(false)}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}
